/* eslint-disable @typescript-eslint/naming-convention */
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Scroll from 'react-scroll';
import { Batch } from '../../../../common/src/domain/batch/batchTableSchema';
import { ITranslateProps, translate } from '../../mixins/translate';
import { IValidationState } from '../../mixins/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { AssetTypes as assetTypes } from '../../utils/enums/AssetTypes';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { ObjectStates } from '../../utils/enums/ObjectStates';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import { isCustomerVersion } from '../../utils/isCustomerVersion';
import { isEemuaOrApi } from '../../utils/isEemuaOrApi';
import {
    LeftTray,
    LeftTrayItem,
    LeftTrayList,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelLink,
    PanelRouterLink,
    PanelWithoutForm,
    ScrollMenu,
    ScrollMenuItem,
    Table,
    TableBody,
    TableData,
    TableHead,
    TableHeadRow,
    TableHeader,
    TableRow,
    Topbar,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { IAsset } from '../interfaces/IAsset';
import { IElement } from '../interfaces/IElement';
import { IMaintenanceMeasure } from '../interfaces/IMaintenanceMeasure';
import { Spinner } from '../spinners';
import { Annotation2D } from '../Surveys/Common/Annotation2D';
import { ReportImage } from '../Surveys/Common/ReportImage';
import { BatchStatusLabel } from '../utils/BatchStatusLabel';
import { batchStatusToColorCode } from '../utils/batchStatusToColorCode';
import { Component } from '../utils/Component';
import { DisplayMessage } from '../utils/displayMessage';
import { DocumentsList } from '../utils/DocumentsList';
import { NullableFormattedDate } from '../utils/NullableFormattedDate';
import { ObjectStatusLabel } from '../utils/ObjectStatusLabel';
import RoleDependent from '../utils/RoleDependent';
import { TagLabels } from '../utils/TagLabels';
import { TimelineChart } from '../utils/TimelineChart';
import { UnitOfMeasurement } from '../utils/UnitOfMeasurement';
import { DetailsDocuments } from './DetailsDocuments';
import { ObjectDetailsAreaMap } from './ObjectDetailsAreaMap';
import { ObjectDetailsGeneralData } from './ObjectDetailsGeneralData';
import { ObjectDetailsRelations } from './ObjectDetailsRelations';
import { ObjectMaintenanceList } from './ObjectMaintenanceList';
import { ObjectSurveyList } from './ObjectSurveyList';

const { scroller } = Scroll;

interface IObjectDetailsProps extends ITranslateProps, IRouterProps {
    dispatch: (prop: any) => void;
    useBatches: boolean;
    role: string;
    isPdokMapLayerAvailable: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
    useObjectRelations: boolean;
    useProductTitle: boolean;
}

interface ICategory {
    id: string;
    name: string;
}

interface IElementProps extends ITranslateProps {
    element: IElement;
    breakDownOpenState: boolean;
    onChangeBreakdownOpenState?: (e: React.MouseEvent<any>, id: string) => void;
    objectId: string;
}

class ElementRowComponent extends Component<IElementProps, Record<string, unknown>> {
    public override render() {
        return (
            <>
                <thead key={this.props.element.id}>
                    <tr className="primary">
                        <th>{this.props.element.code}</th>
                        <th>{this.props.element.name}</th>
                        <th>&nbsp;</th>
                        <th>{this.props.element.location}</th>
                        <th className="text-right element-button">
                            <button
                                type="button"
                                onClick={(e: any) => this.props.onChangeBreakdownOpenState!(e, this.props.element.id)}
                                className={this.props.element.units.length === 0 ? 'hidden' : 'btn btn-primary btn-sm fs10'}
                            >
                                <span className={this.props.breakDownOpenState ? 'fa fa-minus' : 'fa fa-plus'}></span>
                            </button>
                        </th>
                    </tr>
                </thead>
                <tbody className={this.props.breakDownOpenState ? 'object-table-unit' : 'hidden'} key={`${this.props.element.id}tbody`}>
                    {(this.props.element.assets?.length ?? 0) > 0 && (
                        <tr>
                            <td>&nbsp;</td>
                            <td colSpan={4}>
                                <DocumentsList assets={this.props.element.assets!} objectId={this.props.objectId} blueHeader />
                            </td>
                        </tr>
                    )}
                    {this.props.element.units.map((unit) => (
                        <React.Fragment key={unit.id}>
                            <tr className="bg-light darker">
                                <td>{unit.code}</td>
                                <td>{unit.name}</td>
                                <td>
                                    {unit.material}&nbsp;
                                    {unit.quantity}
                                    <UnitOfMeasurement value={unit.quantityUnitOfMeasurement} />
                                </td>
                                <td>{unit.location}</td>
                                <td>&nbsp;</td>
                            </tr>
                            {unit.assets?.length > 0 && (
                                <tr>
                                    <td>&nbsp;</td>
                                    <td colSpan={4}>
                                        <DocumentsList assets={unit.assets} objectId={this.props.objectId} blueHeader />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </>
        );
    }
}

function responseStatusCodeToMessage(statusCode: number | undefined, translateKey: (key: string) => string) {
    switch (statusCode) {
        case 400:
            return translateKey('common.invalidId');
        case 404:
            return translateKey('common.doesNotExistAnymore');
        case 403:
            return translateKey('common.noAccess');
        default:
            return `invalid request status code ${statusCode}`;
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const ElementRow = translate()(ElementRowComponent);

interface IObjectDetailsState extends IValidationState {
    code?: string;
    name?: string;
    location?: string;
    surveryedOn?: Date;
    latitude?: number;
    longitude?: number;
    elements: IElement[];
    inspectionStandardType?: InspectionStandardTypes;
    categories: ICategory[];
    maintenanceMeasures: IMaintenanceMeasure[];
    batches: any;
    metadata?: any;
    clientName?: string;
    surveyorName?: string;
    operatorName?: string;
    ownerName?: string;
    ownerCompanyId?: string;
    objectTypeName?: string;
    breakDownOpenState: any;
    isPublic: boolean;
    usage?: string;
    constructionYear?: number;
    status?: ObjectStates;
    created_at?: Date;
    shapeGeoJson?: string;
    surveyCondition?: string;
    surveyInspectionStandardType?: string;
    canBatchAddSurvey: boolean;
    isObjectDetailsLoading: boolean;
    isMaintenanceLoading: boolean;
    isElementsLoading?: boolean;
    complexName?: string;
    complexCode?: string;
    province?: string;
    municipality?: string;
    startPageAssets: IAsset[];
    labels?: string[];
    useProductTitle?: boolean;
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        useBatches: authentication.useBatches,
        role: authentication.role,
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
        isDroneNoFlyMapNlAvailable: authentication.isDroneNoFlyMapNlAvailable,
        useObjectRelations: authentication.useObjectRelations,
        useProductTitle: authentication.useProductTitle,
    };
}

export class ObjectDetailsComponent extends Component<IObjectDetailsProps, IObjectDetailsState> {
    constructor(props: IObjectDetailsProps) {
        super(props);

        this.state = {
            canBatchAddSurvey: false,
            isPublic: false,
            isObjectDetailsLoading: true,
            isMaintenanceLoading: false,
            submitting: true,
            failed: false,
            saved: false,
            elements: [],
            categories: [],
            maintenanceMeasures: [],
            batches: [],
            startPageAssets: [],
            breakDownOpenState: {},
        };
    }

    public override async componentDidUpdate(prevProps: IObjectDetailsProps) {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            await this.componentDidMount();
        }
    }

    public override async componentDidMount() {
        await this.load();
        await this.loadStartPageAssets();
        await this.loadMaintenanceMeasurements();

        if (this.state.inspectionStandardType !== InspectionStandardTypes.Eemua159) {
            await this.loadElements();
        }
        if (this.props.useBatches && (this.props.role === Roles.Owner || this.props.role === Roles.Administrator)) {
            await this.loadBatches();
        }
    }

    public override render() {
        const isFree = this.state.inspectionStandardType === InspectionStandardTypes.Free;
        const isNEN = this.state.inspectionStandardType === InspectionStandardTypes.Nen2767;

        if (this.state.failed) {
            return (
                <div className="mt20">
                    <DisplayMessage
                        message={`${this.props.t('object.notAvailable')}: ${responseStatusCodeToMessage(this.state.statusCode, this.props.t)}`}
                        messageType="error"
                    />
                </div>
            );
        }

        return (
            <React.Fragment key={this.props.match.params.id}>
                <Topbar>
                    <TopbarLeft>
                        <TopbarCrumbActive title={this.state.name} icon="fa-building" />
                        <TopbarCrumb>
                            <Link to="/objects">{this.props.t('common.objects')}</Link>
                        </TopbarCrumb>
                    </TopbarLeft>
                    <TopbarRight>
                        {(isFree || isNEN) && (
                            <RoleDependent
                                allowed={[Roles.Administrator, Roles.Client]}
                                content={
                                    <PanelLink
                                        href={`/api/objects/${this.props.match.params.id}/export`}
                                        text="Export"
                                        icon="fa fa-download"
                                        small={true}
                                        isDownload={true}
                                    />
                                }
                            />
                        )}
                        <RoleDependent
                            allowed={[Roles.Administrator, Roles.Client, Roles.Owner]}
                            isPublic={this.state.isPublic}
                            content={
                                <PanelRouterLink
                                    to={`/objects/${this.props.match.params.id}/edit`}
                                    text={this.props.t('common.editItem', this.props.t('common.object'))}
                                    small
                                    icon="fa fa-edit"
                                />
                            }
                        />
                    </TopbarRight>
                </Topbar>

                <section id="content" className="table-layout animated fadeIn">
                    <LeftTray>
                        <LeftTrayList>
                            {this.state.code && <LeftTrayItem title={this.props.t('common.code')}>{this.state.code}</LeftTrayItem>}
                            <LeftTrayItem title={this.props.t('common.name')}>{this.state.name}</LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.location')}>{this.state.location}</LeftTrayItem>
                            {this.state.complexName && (
                                <LeftTrayItem title={this.props.t('object.complexName')}>{this.state.complexName}</LeftTrayItem>
                            )}
                            {this.state.complexCode && (
                                <LeftTrayItem title={this.props.t('object.complexCode')}>
                                    <Link to={`/objects?complexCode=${this.state.complexCode}`}>{this.state.complexCode}</Link>
                                </LeftTrayItem>
                            )}
                            {this.state.province && <LeftTrayItem title={this.props.t('common.province')}>{this.state.province}</LeftTrayItem>}
                            {this.state.municipality && (
                                <LeftTrayItem title={this.props.t('common.municipality')}>{this.state.municipality}</LeftTrayItem>
                            )}
                            {this.state.objectTypeName && <LeftTrayItem title="Type">{this.state.objectTypeName}</LeftTrayItem>}
                            <LeftTrayItem title={this.props.t('common.composition')}>
                                {this.props.t(`inspectionStandard.${this.state.inspectionStandardType}`)}
                            </LeftTrayItem>
                            {this.state.usage && <LeftTrayItem title={this.props.t('object.usage')}>{this.state.usage}</LeftTrayItem>}
                            {this.state.constructionYear && (
                                <LeftTrayItem title={this.props.t('object.constructionYear')}>{this.state.constructionYear}</LeftTrayItem>
                            )}
                            {this.state.ownerName && (
                                <LeftTrayItem title={this.props.t('common.owner')}>
                                    {' '}
                                    <Link to={`/objects?ownerId=${this.state.ownerCompanyId}`}>{this.state.ownerName}</Link>
                                </LeftTrayItem>
                            )}
                            {this.state.operatorName && !this.props.useBatches && (
                                <LeftTrayItem title={this.props.t('common.operator')}>{this.state.operatorName}</LeftTrayItem>
                            )}
                            {this.state.surveyorName && !this.props.useBatches && (
                                <LeftTrayItem title={this.props.t('common.surveyor')}>{this.state.surveyorName}</LeftTrayItem>
                            )}
                            <LeftTrayItem
                                title={this.props.useProductTitle ? this.props.t('object.lastProductDate') : this.props.t('object.lastSurveyDate')}
                            >
                                <NullableFormattedDate value={this.state.surveryedOn} />
                            </LeftTrayItem>
                            <LeftTrayItem title={this.props.t('common.status')}>
                                <ObjectStatusLabel value={this.state.status} />
                            </LeftTrayItem>

                            <LeftTrayItem>
                                <TagLabels labels={this.state.labels} urlPathname="/objects" />
                            </LeftTrayItem>
                        </LeftTrayList>

                        <ScrollMenu>
                            <ScrollMenuItem
                                text={this.props.useProductTitle ? this.props.t('object.productList') : this.props.t('object.surveyList')}
                                icon="fa fa-camera"
                                onClick={() => this.scrollTo('surveylist')}
                            />
                            {this.props.useObjectRelations && (
                                <ScrollMenuItem
                                    text={this.props.t('objectRelation.objectRelations')}
                                    icon="fa fa-link"
                                    onClick={() => this.scrollTo('objectRelations')}
                                />
                            )}
                            {this.state.batches && this.state.batches.length > 0 && (
                                <ScrollMenuItem text="Batches" icon="fa fa-suitcase" onClick={() => this.scrollTo('batchlist')} />
                            )}
                            {this.state.latitude && (
                                <ScrollMenuItem text={this.props.t('object.areaMap')} icon="fa fa-globe" onClick={() => this.scrollTo('areamap')} />
                            )}

                            {!isEemuaOrApi(this.state.inspectionStandardType) && this.state.elements.length > 0 && (
                                <ScrollMenuItem
                                    text={this.props.t('object.objectBreakdown')}
                                    icon="fa fa-tasks"
                                    onClick={() => this.scrollTo('objectbreakdown')}
                                />
                            )}
                            <ScrollMenuItem text={this.props.t('survey.documents')} icon="fa fa-book" onClick={() => this.scrollTo('Documents')} />
                            <ScrollMenuItem
                                text={this.props.t('object.generalData')}
                                icon="fa fa-book"
                                onClick={() => this.scrollTo('GeneralData')}
                            />
                            {isCustomerVersion(['zxy', 'rijksvastgoed', 'utrecht', 'rotterdam']) && (
                                <ScrollMenuItem
                                    text={this.props.t('maintenanceMeasure.maintenanceMeasures')}
                                    icon="fa fa-wrench"
                                    onClick={() => this.scrollTo('Maintenance')}
                                />
                            )}
                        </ScrollMenu>
                    </LeftTray>

                    <Tray>
                        {this.state.isObjectDetailsLoading && <Spinner />}

                        {/* 2D model */}
                        {this.state.startPageAssets
                            .filter((asset: IAsset) => asset.type === assetTypes.ImageAnnotation)
                            .map((asset: IAsset, i: number) => (
                                <Annotation2D
                                    key={i}
                                    reportImage={asset.id}
                                    reportImageTitle={asset.description}
                                    objectId={this.props.match.params.id}
                                />
                            ))}

                        {/* Report image */}
                        {this.state.startPageAssets
                            .filter((asset: IAsset) => asset.type === assetTypes.Image)
                            .map((asset: IAsset, i: number) => (
                                <ReportImage key={i} reportImage={asset.id} reportImageTitle={asset.description} />
                            ))}

                        {/* Inspections List*/}
                        <ObjectSurveyList
                            isPublic={this.state.isPublic}
                            canBatchAddSurvey={this.state.canBatchAddSurvey}
                            objectStatus={this.state.status ?? ObjectStates.Deleted}
                            objectId={this.props.match.params.id}
                            useProductTitle={this.props.useProductTitle}
                        />

                        {isCustomerVersion(['zxy', 'rijksvastgoed', 'utrecht', 'rotterdam']) && (
                            <ObjectMaintenanceList
                                objectId={this.props.match.params.id}
                                maintenanceMeasures={this.state.maintenanceMeasures}
                                isLoading={this.state.isMaintenanceLoading}
                                setImplementationYear={async (maintenanceMeasureId) => this.setImplementationYear(maintenanceMeasureId)}
                            />
                        )}

                        {this.props.useObjectRelations && <ObjectDetailsRelations objectId={this.props.match.params.id} />}

                        {/* Batch List*/}
                        {this.state.batches && this.state.batches.length > 0 && (
                            <PanelWithoutForm id="batchlist">
                                <PanelHeading text="Batches" icon="fa fa-suitcase" />

                                <div className="panel-body pn">
                                    <TimelineChart
                                        data={this.state.batches.map((item: any) => ({
                                            title: item.batch.name,
                                            startDate: item.batch.plannedStartDate,
                                            endDate: item.batch.plannedEndDate,
                                            tooltipBodyText: this.getBatchTooltip(item.batch),
                                            backgroundColor: batchStatusToColorCode(item.batch.status),
                                            url: `/batches/${item.batch.id}`,
                                        }))}
                                    />

                                    <div className="p10">
                                        <Table hover>
                                            <TableHead>
                                                <TableHeadRow light>
                                                    <TableHeader>{this.props.t('common.name')}</TableHeader>
                                                    <TableHeader>{this.props.t('batch.plannedDate')} </TableHeader>
                                                    <TableHeader>{this.props.t('batch.implementationDate')}</TableHeader>
                                                    <TableHeader className="text-right">{this.props.t('common.status')}</TableHeader>
                                                </TableHeadRow>
                                            </TableHead>
                                            <TableBody>
                                                {this.state.batches.map((item: any, k: number) => (
                                                    <TableRow key={k} onClick={() => this.rowClicked(`/batches/${item.batch.id}`)}>
                                                        <TableData>{item.batch.name}</TableData>
                                                        <TableData>
                                                            {' '}
                                                            <NullableFormattedDate value={item.batch.plannedStartDate} /> -{' '}
                                                            <NullableFormattedDate value={item.batch.plannedEndDate} />
                                                        </TableData>
                                                        <TableData>
                                                            <NullableFormattedDate value={item.batch.startDate} /> -{' '}
                                                            <NullableFormattedDate value={item.batch.endDate} />
                                                        </TableData>
                                                        <TableData alignRight>
                                                            <BatchStatusLabel value={item.batch.status} />
                                                        </TableData>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </PanelWithoutForm>
                        )}

                        {/* Map */}
                        {this.state.latitude &&
                            this.state.longitude &&
                            this.state.name &&
                            this.props.match.params.id &&
                            this.state.objectTypeName && (
                                <ObjectDetailsAreaMap
                                    latitude={this.state.latitude}
                                    longitude={this.state.longitude}
                                    name={this.state.name}
                                    objectId={this.props.match.params.id}
                                    objectTypeName={this.state.objectTypeName}
                                    shapeGeoJson={this.state.shapeGeoJson}
                                    surveyCondition={this.state.surveyCondition}
                                    surveyInspectionStandardType={this.state.surveyInspectionStandardType}
                                    isDroneNoFlyMapNlAvailable={this.props.isDroneNoFlyMapNlAvailable}
                                    isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                                ></ObjectDetailsAreaMap>
                            )}

                        {!isEemuaOrApi(this.state.inspectionStandardType) && this.state.elements.length > 0 && (
                            <>
                                {/* Object breakdown */}
                                <PanelWithoutForm id="objectbreakdown">
                                    <PanelHeading text={this.props.t('object.objectBreakdown')} icon="fa fa-tasks">
                                        <PanelLink
                                            href={`/api/objects/${this.props.match.params.id}/exportComposition`}
                                            text={this.props.t('common.export')}
                                            isDownload={true}
                                            icon="fa fa-download"
                                            small={true}
                                        />
                                        <PanelButtonLink
                                            small
                                            onClick={() => this.onOpenInspectionChecklist()}
                                            text={this.props.t('object.openInspectionChecklist')}
                                            icon="fa fa-external-link"
                                            mb0={true}
                                        />
                                    </PanelHeading>
                                    <PanelBody>
                                        <a
                                            href="#"
                                            id="unfold_all"
                                            className="btn btn-default btn-xs pull-right bt btn-sm fw600 text-dark mb5 ml5"
                                            onClick={this.onExpandAll.bind(this)}
                                        >
                                            <span className="fa fa fa-plus-square-o"></span> {this.props.t('survey.expandAll')}
                                        </a>
                                        <a
                                            href="#"
                                            id="fold_all"
                                            className="btn btn-default btn-xs pull-right bt btn-sm fw600 text-dark mb5 ml5"
                                            onClick={this.onCollapseAll.bind(this)}
                                        >
                                            <span className="fa fa-minus-square-o"></span> {this.props.t('survey.collapseAll')}
                                        </a>
                                        <table id="object-table" className="table table-striped">
                                            {this.state.categories.length > 0 &&
                                                this.state.categories.map((category, i) => [
                                                    <React.Fragment key={i}>
                                                        <thead>
                                                            <tr className="active mt10">
                                                                <th colSpan={4} className="p10 fw600 fs16">
                                                                    {category.name}{' '}
                                                                </th>
                                                            </tr>
                                                            <tr className="dark">
                                                                <th>{this.props.t('common.code')}</th>
                                                                <th>{this.props.t('common.part')}</th>
                                                                <th></th>
                                                                <th></th>
                                                                <th>&nbsp;</th>
                                                            </tr>
                                                        </thead>
                                                        {this.state.elements
                                                            .filter((element: any) => element.categoryId === category.id)
                                                            .map((element, j) => [
                                                                <ElementRow
                                                                    key={j}
                                                                    element={element}
                                                                    breakDownOpenState={this.state.breakDownOpenState[element.id]}
                                                                    onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                                                    objectId={this.props.match.params.id}
                                                                />,
                                                            ])}
                                                        <tbody>
                                                            <tr>
                                                                <td colSpan={4}>&nbsp;</td>
                                                            </tr>
                                                        </tbody>
                                                    </React.Fragment>,
                                                ])}

                                            {(!this.state.categories || this.state.categories.length === 0) && (
                                                <>
                                                    <thead>
                                                        <tr className="dark">
                                                            <th>{this.props.t('common.code')}</th>
                                                            <th>{this.props.t('common.part')}</th>
                                                            <th>{this.props.t('common.material')}</th>
                                                            <th>{this.props.t('common.location')}</th>
                                                            <th>&nbsp; </th>
                                                        </tr>
                                                    </thead>
                                                    {this.state.elements.map((element, k) => [
                                                        <ElementRow
                                                            key={k}
                                                            element={element}
                                                            breakDownOpenState={this.state.breakDownOpenState[element.id]}
                                                            onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                                            objectId={this.props.match.params.id}
                                                        />,
                                                    ])}
                                                </>
                                            )}
                                        </table>
                                    </PanelBody>
                                </PanelWithoutForm>
                            </>
                        )}

                        <DetailsDocuments objectId={this.props.match.params.id} context="object" />
                        <PanelWithoutForm id="GeneralData">
                            <ObjectDetailsGeneralData
                                objectId={this.props.match.params.id}
                                inspectionStandardType={this.state.inspectionStandardType}
                            />
                        </PanelWithoutForm>

                        {/* Object breakdown print view*/}
                        <div id="popoutContents" className="hidden">
                            <div className="tray tray-center">
                                <PanelHeading text={this.state.name ?? ''} icon="fa fa-building" />
                                <div className="admin-form theme-primary mw800 ">
                                    <div className="panel ">
                                        <form method="post" action="/" id="admin-form">
                                            <div className="panel-body bg-light">
                                                <table className="table table-striped">
                                                    <thead>
                                                        <tr className="dark">
                                                            <th>{this.props.t('common.code')}</th>
                                                            <th>{this.props.t('common.part')}</th>
                                                            <th>{this.props.t('common.location')}</th>
                                                            <th className="text-right">{this.props.t('surveyStatus.completed')}</th>
                                                        </tr>
                                                    </thead>

                                                    {this.state.elements.map((element) => [
                                                        <thead key={element.id}>
                                                            <tr className="primary">
                                                                <th>{element.code}</th>
                                                                <th>{element.name}</th>
                                                                <th>{element.location}</th>
                                                                <th className="text-right">
                                                                    <div className="flightplan-check pull-right">&nbsp; </div>
                                                                </th>
                                                            </tr>
                                                        </thead>,
                                                        <tbody key={`${element.id}tbody`}>
                                                            {element.units.map((unit) => (
                                                                <tr key={unit.id}>
                                                                    <td>{unit.code}</td>
                                                                    <td>{unit.name}</td>
                                                                    <td>{unit.location}</td>
                                                                    <td className="text-right">
                                                                        <div className="flightplan-check pull-right">&nbsp; </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>,
                                                    ])}
                                                </table>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Tray>
                </section>
            </React.Fragment>
        );
    }

    private getBatchTooltip(batch: Pick<Batch, 'id' | 'startDate' | 'endDate' | 'plannedStartDate' | 'plannedEndDate'>) {
        return this.props.t(
            'batch.timelineTooltip',
            batch.plannedStartDate ? moment(batch.plannedStartDate).format('ll') : '',
            batch.plannedEndDate ? moment(batch.plannedEndDate).format('ll') : '',
            batch.startDate ? moment(batch.startDate).format('ll') : '',
            batch.endDate ? moment(batch.endDate).format('ll') : '',
        );
    }

    private onOpenInspectionChecklist() {
        const popoutWindow = window.open('/popup.html', undefined, 'height=600,width=800,status=yes,toolbar=no,menubar=no,location=no');
        const onloadHandler = () => {
            const container = popoutWindow!.document.getElementById('content');
            if (container) {
                container.innerHTML = window.document.getElementById('popoutContents')!.innerHTML;
            }
        };

        popoutWindow!.onload = onloadHandler;
    }

    private onChangeBreakdownOpenState(e: any, elementId: string) {
        e.preventDefault();
        const { breakDownOpenState } = this.state;
        breakDownOpenState[elementId] = !breakDownOpenState[elementId];
        void this.setState({ breakDownOpenState });
    }

    private async load() {
        const url = `/api/objects/${this.props.match.params.id}`;
        const result = await fetchGet(url, {}, [], [400, 403, 404]);
        await this.setState({ isObjectDetailsLoading: false });
        if ([404, 403, 400].includes(result.status)) {
            await this.setState({ failed: true, statusCode: result.status });
        } else {
            await this.setState({ failed: false });
            await this.setState(result.json);
        }
    }

    private async loadElements() {
        await this.setState({ isElementsLoading: true });
        const url = `/api/objects/${this.props.match.params.id}/elements`;
        const result = await fetchGet(url, { includeObjectDocuments: 'true' });
        await this.setState({ elements: result.json, isElementsLoading: false });
    }

    private async loadBatches() {
        const url = `/api/objects/${this.props.match.params.id}/batches`;
        const result = await fetchGet(url);
        await this.setState({ batches: result.json });
    }

    private async loadMaintenanceMeasurements() {
        await this.setState({ isMaintenanceLoading: true });
        const url = `/api/objects/${this.props.match.params.id}/maintenanceMeasures`;
        const result = await fetchGet(url, {});
        await this.setState({ maintenanceMeasures: result.json, isMaintenanceLoading: false });
    }

    private async setImplementationYear(maintenanceMeasureId: string) {
        const url = `/api/maintenancemeasures/${maintenanceMeasureId}`;
        const data = {
            implementationYear: new Date().getFullYear(),
        };
        await fetchPost(url, {}, data);
        await this.loadElements();
    }

    private async loadStartPageAssets() {
        const url = `/api/objects/${this.props.match.params.id}/assets?onstartpage=true&types=${assetTypes.ImageAnnotation},${assetTypes.Image},${assetTypes.Image360}`;
        const result = await fetchGet(url);
        await this.setState({ startPageAssets: result.json });
    }

    private scrollTo(name: string) {
        scroller.scrollTo(name, {
            duration: 1500,
            delay: 100,
            smooth: true,
        });
    }

    private rowClicked(url: string) {
        this.props.navigate(url);
    }

    private onExpandAll(e: any) {
        e.preventDefault();
        const breakDownOpenState = {} as any;
        this.state.elements.forEach((element) => {
            breakDownOpenState[element.id] = true;
        });

        void this.setState({ breakDownOpenState });
    }

    private onCollapseAll(e: any) {
        e.preventDefault();
        void this.setState({ breakDownOpenState: {} });
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(ObjectDetailsComponent as any) as any);
